import React from "react";
import { EmptyState, Icon, LogoMark, confirmDialog, useNarrowScreen, moveJob, notify } from "./registry";

/* "can we do Thursday?" → a date. Weekday names (next occurrence), tomorrow,
   next week (Monday), and M/D. Null when the text names no day. */
function parseWhen(text: string): { ymd: string; label: string } | null {
  const t = String(text || "").toLowerCase();
  const now = new Date(); now.setHours(12, 0, 0, 0);
  const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  let d: Date | null = null;
  const md = t.match(/\b(\d{1,2})\/(\d{1,2})\b/);
  if (md) { d = new Date(now.getFullYear(), Number(md[1]) - 1, Number(md[2]), 12); if (d.getTime() < now.getTime() - 86400000) d.setFullYear(d.getFullYear() + 1); }
  else if (/\btomorrow\b/.test(t)) { d = new Date(now); d.setDate(d.getDate() + 1); }
  else if (/\bnext week\b/.test(t)) { d = new Date(now); d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7)); }
  else {
    const i = days.findIndex(n => new RegExp("\\b" + n.slice(0, 3) + "(" + n.slice(3) + ")?\\b").test(t));
    if (i >= 0) { d = new Date(now); const delta = (i - d.getDay() + 7) % 7 || 7; d.setDate(d.getDate() + delta); }
  }
  if (!d || isNaN(d.getTime())) return null;
  const ymd = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
  return { ymd, label: d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) };
}
import { downscaleToDataUrl } from "./lib/img";
/* ============================================================
   Fence Works — Messages
   • Customer Hub conversations — REAL threads from the
     conversations/messages tables (what customers send from their
     hub lands here; replies show up on their hub).
   • Texts — 2-way SMS with customers through the business texting
     number (the AI receptionist's number, or one we provision).
     Optional forwarding mirrors inbound texts to the owner's cell;
     a reply from the cell is relayed out through the business number.
   • Fence Works team ↔ operator (local demo thread for now)
   Cloud-backed when signed in; local mode falls back to the old
   in-store threads so demo/offline keeps working.
   ============================================================ */
const digits = (s: any) => (s || "").replace(/\D/g, "");   // phone → digits only, for matching

/* ============================================================
   Business-texting A2P 10DLC registration — form + status badge.
   US carriers BLOCK business SMS until the sender's brand + campaign are
   registered (A2P 10DLC). This collects the short business profile Twilio
   needs, kicks off DB.smsRegister(), then shows a live status badge:
     • provisioning   → "Setting up…"
     • pending        → "Pending approval (usually 1–2 days)" — inbound works
                         now, outbound unlocks at approval
     • active         → "Active ✓"
     • rejected/none  → the form (with the reject reason, if any)
   Prefills everything it can from company settings + the automation templates
   so it's a short form. Reuses the existing card / input / kicker classes.
   ============================================================ */
function fillTpl(t: string, company: string) {
  return String(t || "")
    .replace(/\{customer\}/g, "Sam")
    .replace(/\{company\}/g, company || "our team")
    .replace(/\{link\}/g, "https://fenceworks.ai/q/abc")
    .replace(/\{date\}/g, "Sat, Jun 21")
    .replace(/\{time\}/g, "Saturday 10am");
}
function defaultSamples(company: string) {
  // Build verifiable sample messages from the live automation templates (what the
  // contractor actually sends), with placeholders filled to real-looking copy.
  var ai: any = (window as any).VFP_AI;
  var picks = ["missed_call_text", "estimate_confirm", "appt_reminder", "review_request", "ar_followup"];
  var out: string[] = [];
  if (ai && typeof ai.defaultTemplate === "function") {
    picks.forEach(function (id) { try { var t = ai.defaultTemplate({ id: id }); if (t) out.push(fillTpl(t, company)); } catch (e) {} });
  }
  if (!out.length) {
    out = [
      "Hi Sam, sorry we missed your call! Get an instant fence estimate here: https://fenceworks.ai/q/abc",
      "Hi Sam, confirming your free fence estimate with " + (company || "our team") + " on Sat, Jun 21. Reply C to confirm.",
    ];
  }
  // de-dup + cap at Twilio's practical sample count
  return out.filter(function (v, i) { return out.indexOf(v) === i; }).slice(0, 5);
}

function SmsRegistration({ store, DB, live }: any) {
  var company = (store.company) || {};
  var settings = (store.settings) || {};
  var reg = (settings.sms) || {};
  var companyName = company.name || "";
  var addrParts = [company.street || company.address, company.city, company.state, company.zip].filter(Boolean);
  var [status, setStatus] = React.useState<any>(reg.status || "none");
  var [rejectReason, setRejectReason] = React.useState<any>(reg.rejectReason || "");
  var [number, setNumber] = React.useState<any>(reg.number || null);
  var [busy, setBusy] = React.useState<any>(false);
  var [err, setErr] = React.useState<any>("");
  var savedProfile = (reg.businessProfile) || {};
  var [form, setForm] = React.useState<any>({
    legalName: savedProfile.legalName || companyName,
    ein: savedProfile.ein || "",
    address: savedProfile.address || addrParts.join(", "),
    website: savedProfile.website || company.website || "",
    contactName: savedProfile.contactName || "",
    contactEmail: savedProfile.contactEmail || company.email || "",
    contactPhone: savedProfile.contactPhone || company.phone || "",
    optInDescription: savedProfile.optInDescription || ("Customers opt in to texts from " + (companyName || "us") + " for appointment reminders, estimates and account updates when they request a quote or share their number. Msg & data rates may apply. Reply STOP to opt out, HELP for help."),
    useCase: savedProfile.useCase || "MIXED",
    sampleMessages: (savedProfile.sampleMessages && savedProfile.sampleMessages.length ? savedProfile.sampleMessages.join("\n") : defaultSamples(companyName).join("\n")),
  });
  var set = function (k: string, v: any) { setForm(function (f: any) { var n = Object.assign({}, f); n[k] = v; return n; }); };

  // poll the live status while pending, so the badge self-updates (and we persist
  // the change locally for the rest of the app to read).
  React.useEffect(function () {
    if (!live || !DB || !DB.smsStatus) return;
    var apply = function (s: any) {
      if (!s) return;
      setStatus(s.status || "none"); setRejectReason(s.rejectReason || ""); setNumber(s.number || null);
      store.setSettings({ sms: Object.assign({}, reg, s) });
    };
    DB.smsStatus().then(apply).catch(function () {});
    if ((reg.status || status) !== "pending") return;
    var t = setInterval(function () { DB.smsStatus().then(apply).catch(function () {}); }, 30000);
    return function () { clearInterval(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, reg.status]);

  async function submit() {
    setErr("");
    if (!live || !DB || !DB.smsRegister) { setErr("Business texting setup needs the live service (app.fenceworks.ai)."); return; }
    var samples = String(form.sampleMessages || "").split("\n").map(function (s: string) { return s.trim(); }).filter(Boolean);
    if (!form.legalName.trim()) { setErr("Your legal business name is required."); return; }
    if (!samples.length) { setErr("Add at least one sample message customers will receive."); return; }
    setBusy(true);
    try {
      var profile = {
        legalName: form.legalName.trim(), ein: form.ein.trim(), address: form.address.trim(),
        website: form.website.trim(), contactName: form.contactName.trim(),
        contactEmail: form.contactEmail.trim(), contactPhone: form.contactPhone.trim(),
        optInDescription: form.optInDescription.trim(), useCase: form.useCase, sampleMessages: samples,
      };
      var s = await DB.smsRegister(profile);
      setStatus((s && s.status) || "pending"); setNumber((s && s.number) || null); setRejectReason((s && s.rejectReason) || "");
      store.setSettings({ sms: Object.assign({}, reg, s) });
      store.addActivity("Business texting registration submitted" + (s && s.number ? " · " + s.number : ""));
    } catch (e: any) { setErr(String((e && e.message) || e)); } finally { setBusy(false); }
  }

  var showForm = status === "none" || status === "rejected";

  return (
    <div style={{ maxWidth: 600 }}>
      {/* status badge */}
      {!showForm && (
        <div className="card pad" style={{ marginBottom: 16 }}>
          <div className="row between" style={{ flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
            <span className="row gap-8 kicker kicker-teal"><Icon name="phone" size={14} />Business texting</span>
            {status === "active"
              ? <span className="chip green" style={{ fontWeight: 700 }}>Active ✓</span>
              : <span className="chip amber" style={{ fontWeight: 700 }}>Pending approval</span>}
          </div>
          {number && <p className="mono" style={{ fontSize: 14, fontWeight: 700, margin: "0 0 8px" }}>{number}</p>}
          {status === "active"
            ? <p className="muted" style={{ fontSize: 13, margin: 0 }}>Your number is registered and approved — texts send and receive normally.</p>
            : <p className="muted" style={{ fontSize: 13, margin: 0 }}>
                Pending approval (usually 1–2 days). <b style={{ color: "var(--fg-2)" }}>Inbound texts work now</b> — customers can reach you and replies land here. Outbound texting unlocks the moment carriers approve your registration.
              </p>}
          {err && <p style={{ color: "var(--red)", fontSize: 12, marginTop: 10 }}>{err}</p>}
        </div>
      )}

      {/* registration form (none / rejected) */}
      {showForm && (
        <div className="card pad">
          <div className="row gap-8 kicker kicker-teal" style={{ marginBottom: 8 }}><Icon name="phone" size={14} />Set up business texting</div>
          {status === "rejected" && rejectReason
            ? <div className="pe-callout" style={{ marginBottom: 12, borderColor: "var(--red)" }}>
                <p className="muted" style={{ margin: 0, fontSize: 12.5 }}><b style={{ color: "var(--red)" }}>Action needed:</b> {rejectReason} Update the details below and resubmit.</p>
              </div>
            : <p className="muted" style={{ fontSize: 13, margin: "0 0 14px" }}>
                US carriers require businesses to register before texting customers (A2P 10DLC). We've pre-filled what we can — review, then submit. Approval usually takes 1–2 days.
              </p>}
          {!live && <p className="faint" style={{ fontSize: 12, margin: "0 0 12px" }}>Registration needs the live service (app.fenceworks.ai). It's a no-op in this demo.</p>}

          <label className="field-label">Legal business name</label>
          <input className="input" value={form.legalName} onChange={function (e: any) { set("legalName", e.target.value); }} placeholder="Acme Fence LLC" />
          <div className="row gap-8" style={{ flexWrap: "wrap", marginTop: 10 }}>
            <div style={{ flex: "1 1 200px" }}>
              <label className="field-label">EIN (tax ID)</label>
              <input className="input" value={form.ein} onChange={function (e: any) { set("ein", e.target.value); }} placeholder="12-3456789" />
            </div>
            <div style={{ flex: "1 1 200px" }}>
              <label className="field-label">Use case</label>
              <select className="input" value={form.useCase} onChange={function (e: any) { set("useCase", e.target.value); }}>
                <option value="MIXED">Mixed (reminders, estimates, updates)</option>
                <option value="CUSTOMER_CARE">Customer care</option>
                <option value="MARKETING">Marketing</option>
                <option value="LOW_VOLUME">Low volume mixed</option>
              </select>
            </div>
          </div>
          <label className="field-label" style={{ marginTop: 10 }}>Business address</label>
          <input className="input" value={form.address} onChange={function (e: any) { set("address", e.target.value); }} placeholder="123 Main St, Springfield, WI 53000" />
          <div className="row gap-8" style={{ flexWrap: "wrap", marginTop: 10 }}>
            <div style={{ flex: "1 1 200px" }}>
              <label className="field-label">Website</label>
              <input className="input" value={form.website} onChange={function (e: any) { set("website", e.target.value); }} placeholder="https://acmefence.com" />
            </div>
            <div style={{ flex: "1 1 200px" }}>
              <label className="field-label">Contact name</label>
              <input className="input" value={form.contactName} onChange={function (e: any) { set("contactName", e.target.value); }} placeholder="Jane Doe" />
            </div>
          </div>
          <div className="row gap-8" style={{ flexWrap: "wrap", marginTop: 10 }}>
            <div style={{ flex: "1 1 200px" }}>
              <label className="field-label">Contact email</label>
              <input className="input" type="email" value={form.contactEmail} onChange={function (e: any) { set("contactEmail", e.target.value); }} placeholder="jane@acmefence.com" />
            </div>
            <div style={{ flex: "1 1 200px" }}>
              <label className="field-label">Contact phone</label>
              <input className="input" type="tel" value={form.contactPhone} onChange={function (e: any) { set("contactPhone", e.target.value); }} placeholder="(555) 555-5555" />
            </div>
          </div>
          <label className="field-label" style={{ marginTop: 10 }}>Sample messages (one per line)</label>
          <textarea className="textarea" rows={5} value={form.sampleMessages} onChange={function (e: any) { set("sampleMessages", e.target.value); }} />
          <label className="field-label" style={{ marginTop: 10 }}>How customers opt in</label>
          <textarea className="textarea" rows={3} value={form.optInDescription} onChange={function (e: any) { set("optInDescription", e.target.value); }} />

          {err && <p style={{ color: "var(--red)", fontSize: 12, marginTop: 10 }}>{err}</p>}
          <div className="row gap-8" style={{ alignItems: "center", marginTop: 14 }}>
            <button className="btn btn-primary" disabled={busy || !live} onClick={submit}>
              <Icon name="phone" size={15} />{busy ? "Submitting…" : (status === "rejected" ? "Resubmit registration" : "Submit & get my number")}
            </button>
            <span className="faint" style={{ fontSize: 11.5 }}>Approval usually takes 1–2 days.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function MessagesPage({ store, go, params }: any) {
  const DB: any = window.VFP_DB;
  // the sandbox has no cloud conversations — its threads live on the local store
  const sandbox = !!((window as any).VFP_DEMO && (window as any).VFP_DEMO.active);
  const cloud = !sandbox && !!(DB && DB.ready && DB.chat);
  const sms = !!(DB && DB.sms);
  const paid = (window.VFP_PLAN ? window.VFP_PLAN.get(store).id : "free") !== "free";
  const customers = store.customers || [];
  const threadsLocal = store.threads || {};
  const [tab, setTab] = React.useState<any>("customers");
  const [convos, setConvos] = React.useState<any[]>([]);
  const [activeId, setActiveId] = React.useState<any>(null);   // conversation id (cloud) or customer id (local)
  // phone: conversations first, one conversation on tap, Back to the list
  const narrow = useNarrowScreen(760);   // where .msg-layout stacks
  const [pane, setPane] = React.useState<any>("list");

  const [msgs, setMsgs] = React.useState<any[]>([]);
  const [text, setText] = React.useState<any>("");
  const [loading, setLoading] = React.useState<any>(cloud);
  const [err, setErr] = React.useState<any>("");
  const [listErr, setListErr] = React.useState<any>("");   // conversation list failed to load (retryable)
  const [msgsErr, setMsgsErr] = React.useState<any>("");   // active thread failed to load (retryable)
  const bodyRef = React.useRef<any>(null);

  /* ---- SMS ("Texts") state — config + threads, mirrors the cloud chat shape ---- */
  const localSms = (store.settings && store.settings.sms) || {};
  // A2P 10DLC: the thread/compose UI is only usable once the registration is
  // APPROVED (carriers block outbound before that). Until then we show the
  // registration form / pending badge instead (SmsRegistration). Pre-A2P
  // workspaces have no status → treat as "needs registration".
  const a2pActive = localSms.status === "active" && !!localSms.number;
  const [smsCfg, setSmsCfg] = React.useState<any>({ number: localSms.number || null, forwardTo: localSms.forwardTo || "", enabled: !!localSms.enabled });
  const [smsThreads, setSmsThreads] = React.useState<any[]>([]);
  const [smsActiveId, setSmsActiveId] = React.useState<any>(null);
  const [smsMsgs, setSmsMsgs] = React.useState<any[]>([]);
  const [smsBusy, setSmsBusy] = React.useState<any>(false);   // provision/release in flight
  const [fwdInput, setFwdInput] = React.useState<any>(localSms.forwardTo || "");

  const custById = React.useMemo(() => {
    const m: any = {}; customers.forEach((c: any) => { m[c.id] = c; }); return m;
  }, [customers]);
  // phone → customer, for auto-linking inbound texts to the matching customer
  const custByPhone = React.useMemo(() => {
    const m: any = {}; customers.forEach((c: any) => { const d = digits(c.phone); if (d) m[d] = c; }); return m;
  }, [customers]);

  /* ---- cloud: conversation list (poll lightly while the screen is open) ---- */
  const refreshList = React.useCallback(() => {
    if (!cloud) return;
    DB.chat.list().then((rows: any[]) => {
      setConvos(rows); setLoading(false); setListErr("");
      setActiveId((cur: any) => cur || (rows[0] && rows[0].id) || null);
    }).catch((e: any) => { setListErr(String(e && e.message || e) || "couldn't load"); setLoading(false); });
  }, [cloud]);
  React.useEffect(() => {
    if (!cloud) return;
    refreshList();
    const t = setInterval(refreshList, 10000);
    return () => clearInterval(t);
  }, [cloud, refreshList]);

  /* ---- cloud: active thread messages (poll while open). A thread is the
     customer's MERGED set of conversations — legacy duplicates (one per hub
     message before the by-customer lookup fix) render as one stream. ---- */
  const idsForActive = React.useCallback(() => {
    const th: any = threadsRef.current.find((x: any) => x.id === activeId);
    return (th && th.allIds) || (activeId ? [activeId] : []);
  }, [activeId]);
  const refreshMsgs = React.useCallback(() => {
    if (!cloud || !activeId) return;
    const ids = idsForActive();
    // a failed read must not render as "No messages yet" — surface it with a retry
    Promise.all(ids.map((id: string) => DB.chat.messages(id)))
      .then((lists: any[]) => {
        const all = ([] as any[]).concat(...lists);
        all.sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
        // keep any not-yet-persisted optimistic bubbles (they carry a localId)
        setMsgs((cur: any[]) => all.concat(cur.filter((m: any) => m.localId)));
        setMsgsErr("");
      }).catch((e: any) => { setMsgsErr(String((e && e.message) || e) || "couldn't load"); });
  }, [cloud, activeId, idsForActive]);
  React.useEffect(() => {
    if (!cloud || !activeId) return;
    setMsgs([]); setMsgsErr(""); refreshMsgs();
    // opening a thread marks it read → sidebar/customer badges clear
    if (!String(activeId).startsWith("new-")) {
      Promise.all(idsForActive().map((id: string) => DB.chat.markRead(id).catch(() => {})))
        .then(() => window.dispatchEvent(new Event("fw:unread")));
    }
    const t = setInterval(refreshMsgs, 6000);
    return () => clearInterval(t);
  }, [cloud, activeId, refreshMsgs]);

  /* ---- SMS: load config (live status wins over the local demo copy) ---- */
  const refreshSmsCfg = React.useCallback(() => {
    if (!sms || !paid) return;
    DB.sms.status().then((s: any) => {
      if (!s) return;
      setSmsCfg({ number: s.number || null, forwardTo: s.forwardTo || "", enabled: !!s.enabled });
      setFwdInput((cur: any) => cur || s.forwardTo || "");
    }).catch(() => {});
  }, [sms, paid]);
  React.useEffect(() => { refreshSmsCfg(); }, [refreshSmsCfg]);

  /* ---- SMS: thread list (poll while open), one row per phone/customer ---- */
  const refreshSms = React.useCallback(() => {
    if (!sms || !paid || !smsCfg.number) return;
    DB.sms.list().then((rows: any[]) => {
      setSmsThreads(rows || []);
      setSmsActiveId((cur: any) => cur || (rows && rows[0] && rows[0].id) || null);
    }).catch(() => {});
  }, [sms, paid, smsCfg.number]);
  React.useEffect(() => {
    if (tab !== "texts" || !sms || !paid || !smsCfg.number) return;
    refreshSms();
    const t = setInterval(refreshSms, 8000);
    return () => clearInterval(t);
  }, [tab, sms, paid, smsCfg.number, refreshSms]);

  /* ---- SMS: active thread messages (poll while open) ---- */
  const refreshSmsMsgs = React.useCallback(() => {
    if (!sms || !smsActiveId || String(smsActiveId).startsWith("new-")) return;
    DB.sms.messages(smsActiveId).then((rows: any[]) => setSmsMsgs(rows || [])).catch(() => {});
  }, [sms, smsActiveId]);
  React.useEffect(() => {
    if (tab !== "texts" || !sms || !smsActiveId) return;
    setSmsMsgs([]); refreshSmsMsgs();
    if (!String(smsActiveId).startsWith("new-")) {
      DB.sms.markRead(smsActiveId).then(() => window.dispatchEvent(new Event("fw:unread"))).catch(() => {});
    }
    const t = setInterval(refreshSmsMsgs, 6000);
    return () => clearInterval(t);
  }, [tab, sms, smsActiveId, refreshSmsMsgs]);

  /* ---- deep link from Customers → "Open thread" ---- */
  React.useEffect(() => {
    const cid = params && params.customerId;
    if (!cid) return;
    setTab("customers");
    if (!cloud) { setActiveId(cid); return; }
    const cust = custById[cid];
    if (!cust) return;
    DB.chat.ensureForCustomer(cust).then((c: any) => { refreshList(); setActiveId(c.id); }).catch((e: any) => setErr(String(e && e.message || e)));
  }, [params && params.customerId, cloud]);

  /* ---- deep link into a SMS thread. Driven by ?smsPhone (a raw number) or
     ?smsCustomerId (a customer to text). NOTE: a bare ?customerId still opens
     the Customers hub thread — we don't hijack it. ---- */
  React.useEffect(() => {
    if (!paid) return;
    const cid = params && params.smsCustomerId;
    let phone = (params && params.smsPhone) || (cid && custById[cid] && custById[cid].phone) || "";
    if (!phone) return;
    setTab("texts");
    const d = digits(phone);
    const existing = smsThreads.find((t: any) => digits(t.phone) === d);
    setSmsActiveId(existing ? existing.id : "new-" + d);   // virtual thread until the first text is sent
  }, [params && params.smsPhone, params && params.smsCustomerId, paid, smsThreads.length]);

  /* ---- thread list: ONE row per customer (their convo rows merged) + customers
     you haven't messaged yet ---- */
  const isUnreadRow = (c: any) => {
    const d = c.data || {};
    return d.last_role === "customer" && (!d.contractor_read_at || (c.last_at && c.last_at > d.contractor_read_at));
  };
  const byCustomer: any = {};
  convos.forEach((c: any) => {
    const key = c.customer_id || c.id;
    (byCustomer[key] = byCustomer[key] || []).push(c);
  });
  const convoCustomerIds = new Set(convos.map((c: any) => c.customer_id).filter(Boolean));
  const threads = cloud
    ? [
        ...Object.entries(byCustomer).map(([key, group]: any) => {
          group.sort((a: any, b: any) => String(b.last_at || "").localeCompare(String(a.last_at || "")));
          const primary = group[0];
          const cust = custById[primary.customer_id] || {};
          const unread = group.some(isUnreadRow);
          return {
            id: primary.id, allIds: group.map((g: any) => g.id), kind: "convo", customerId: primary.customer_id,
            unread: unread && primary.id !== activeId,
            name: cust.name || primary.email || primary.phone || "Customer",
            sub: cust.address || primary.email || primary.phone || "",
            lastAt: primary.last_at ? new Date(primary.last_at).getTime() : 0,
          };
        }),
        ...customers.filter((c: any) => !convoCustomerIds.has(c.id)).map((c: any) => ({ id: "new-" + c.id, allIds: [], kind: "new", customerId: c.id, name: c.name || "Customer", sub: c.address || c.email || "", lastAt: 0 })),
      ].sort((a: any, b: any) => b.lastAt - a.lastAt)
    : customers.map((c: any) => ({ id: c.id, kind: "local", customerId: c.id, name: c.name || "Customer", sub: c.address || c.email || "", lastAt: lastAt(threadsLocal[c.id] || []) }))
        .sort((a: any, b: any) => b.lastAt - a.lastAt);
  const threadsRef = React.useRef<any[]>([]);
  threadsRef.current = threads;

  /* ---- SMS thread rows: one per phone, titled by the matching customer ---- */
  const isUnreadSms = (t: any) => {
    const d = t.data || {};
    return d.last_role === "customer" && (!d.contractor_read_at || (t.last_at && t.last_at > d.contractor_read_at));
  };
  const smsRows = smsThreads.map((t: any) => {
    const cust = (t.customer_id && custById[t.customer_id]) || custByPhone[digits(t.phone)] || {};
    return {
      id: t.id, kind: "sms", phone: t.phone, customerId: cust.id,
      unread: isUnreadSms(t) && t.id !== smsActiveId,
      name: cust.name || t.phone || "Unknown number",
      sub: cust.name ? t.phone : (cust.address || ""),
      lastAt: t.last_at ? new Date(t.last_at).getTime() : 0,
    };
  }).sort((a: any, b: any) => b.lastAt - a.lastAt);
  // a deep-linked phone with no thread yet → a virtual "new-" row at the top
  if (smsActiveId && String(smsActiveId).startsWith("new-") && !smsRows.some((r: any) => r.id === smsActiveId)) {
    const d = smsActiveId.slice(4);
    const cust = custByPhone[d] || {};
    smsRows.unshift({ id: smsActiveId, kind: "sms", phone: cust.phone || d, customerId: cust.id, unread: false, name: cust.name || (cust.phone || d), sub: cust.name ? (cust.phone || d) : "", lastAt: Date.now() });
  }

  /* ---- Fence Works Team = the SAME thread as the help bubble (support_*
     tables via /support/*). Live → poll it while the tab is open; no live
     service → the local demo thread, with sends handed off to email. ---- */
  const supportLive = !!(DB && DB.support && DB.support.enabled && DB.support.enabled());
  const [supMsgs, setSupMsgs] = React.useState<any[]>([]);
  const [supOwner, setSupOwner] = React.useState<any>("the Fence Works team");
  const refreshSupport = React.useCallback(() => {
    if (!supportLive) return;
    DB.support.me().then((d: any) => {
      if (!d) return;
      setSupMsgs(d.messages || []);
      if (d.owner && d.owner.name) setSupOwner(d.owner.name);
      if (d.thread && d.thread.unread) DB.support.read().then(() => window.dispatchEvent(new Event("fw:support")));
    }).catch(() => {});
  }, [supportLive]);
  React.useEffect(() => {
    if (tab !== "team" || !supportLive) return;
    refreshSupport();
    const t = setInterval(refreshSupport, 8000);
    return () => clearInterval(t);
  }, [tab, supportLive, refreshSupport]);
  const teamMsgs = supportLive
    ? supMsgs.map((m: any) => ({ from: m.role === "user" ? "me" : "team", text: m.body, at: m.created_at }))
    : (threadsLocal.__team || [
      { from: "team", text: "👋 Welcome to Fence Works! This is a direct line to the founder — questions, bugs, feature requests. Sending here opens your email app; sign in on app.fenceworks.ai for live in-app replies.", at: Date.now() - 86400000 },
    ]);

  const active = tab === "team"
    ? { id: "__team", kind: "team", name: "Fence Works Team", sub: supportLive ? "Goes straight to " + supOwner + " · replies here + by email" : "Product support & updates" }
    : tab === "texts"
      ? (smsRows.find((t: any) => t.id === smsActiveId) || smsRows[0] || null)
      : threads.find((t: any) => t.id === activeId) || threads[0] || null;

  // local threads (sandbox / offline): reading a thread clears its unread ping
  const seenId = !cloud && tab === "customers" && active && active.kind === "local" ? active.customerId : null;
  React.useEffect(() => {
    if (!seenId) return;
    try {
      const seen = JSON.parse(localStorage.getItem("fw_msgs_seen_v1") || "{}");
      seen[seenId] = Date.now();
      localStorage.setItem("fw_msgs_seen_v1", JSON.stringify(seen));
    } catch (e) {}
    window.dispatchEvent(new Event("fw:unread"));
  }, [seenId, (threadsLocal[seenId || ""] || []).length]);

  const activeMsgs = tab === "team"
    ? teamMsgs.map((m: any) => ({ mine: m.from === "me", body: m.text, at: m.at }))
    : tab === "texts"
      ? smsMsgs.map((m: any) => ({ mine: m.role !== "customer", body: m.body, at: m.created_at }))
      : !cloud
        ? (threadsLocal[(active && active.customerId) || ""] || []).map((m: any) => ({ mine: m.from !== "customer", body: m.text, at: m.at }))
        : msgs.map((m: any) => ({ mine: m.role !== "customer", body: m.body, image: m.image, at: m.created_at }));

  const fileRef = React.useRef<any>(null);
  React.useEffect(() => {
    const el = bodyRef.current; if (el) el.scrollTop = el.scrollHeight;
  }, [activeMsgs.length, activeId, smsActiveId, tab]);

  async function send(image?: string) {
    const body = text.trim();
    if ((!body && !image) || !active) return;
    setErr("");
    // the input is cleared only once the send LANDS — on failure the optimistic
    // bubble comes back out and the text stays in the box so Send is the retry
    const localId = "loc-" + Date.now() + "-" + Math.floor(Math.random() * 1e4);
    const dropLocal = (setter: any) => setter((m: any[]) => m.filter((x: any) => x.localId !== localId));
    const failed = (e: any) => setErr("Couldn't send — " + String((e && e.message) || e) + ". Your message is still in the box; tap Send to retry.");
    if (tab === "team") {
      if (!supportLive) {
        // no live service: keep the note locally AND hand it to email so a
        // person actually sees it (no more fake "we've logged it" auto-reply)
        store.addMessage("__team", { from: "me", text: body, at: Date.now() });
        setText("");
        window.location.href = "mailto:rob@tryfenceworks.com?subject=" + encodeURIComponent("Fence Works help") + "&body=" + encodeURIComponent(body);
        return;
      }
      try {
        setSupMsgs((m: any[]) => [...m, { role: "user", body, created_at: new Date().toISOString(), localId }]);   // optimistic
        await DB.support.send(body, "messages");
        setText("");
        window.dispatchEvent(new Event("fw:support"));
        refreshSupport();
      } catch (e: any) { dropLocal(setSupMsgs); failed(e); }
      return;
    }
    if (tab === "texts") {
      if (!sms) { setErr("Texting needs the live service."); return; }
      try {
        // existing thread → send by id; deep-linked new thread → send by phone
        const target = String(active.id).startsWith("new-") ? (active.phone || active.id.slice(4)) : active.id;
        setSmsMsgs((m: any[]) => [...m, { role: "company", body, created_at: new Date().toISOString(), localId }]);   // optimistic
        await DB.sms.send(target, body);
        setText("");
        refreshSms(); refreshSmsMsgs();
      } catch (e: any) { dropLocal(setSmsMsgs); failed(e); }
      return;
    }
    if (!cloud) { store.addMessage(active.customerId, { from: "company", text: body, at: Date.now() }); setText(""); return; }
    try {
      let convoId = active.id;
      if (active.kind === "new") {
        const c = await DB.chat.ensureForCustomer(custById[active.customerId]);
        convoId = c.id; setActiveId(c.id); refreshList();
      }
      setMsgs((m: any[]) => [...m, { role: "company", body, image, created_at: new Date().toISOString(), localId }]);   // optimistic
      await DB.chat.send(convoId, body, image);
      setText("");
      setMsgs((m: any[]) => m.map((x: any) => x.localId === localId ? { ...x, localId: undefined } : x));   // persisted
      refreshMsgs();
    } catch (e: any) { dropLocal(setMsgs); failed(e); }
  }
  async function attachPhoto(file: File) {
    if (!file) return;
    try { send(await downscaleToDataUrl(file)); } catch (e: any) { setErr("Couldn't attach that image"); }
  }

  /* ---- SMS config helpers (release / forwarding) ---- */
  // Note: getting a number is now part of the A2P 10DLC registration flow
  // (SmsRegistration → DB.smsRegister), which buys + registers the number so
  // carriers don't block outbound. The old direct "provision" call is gone.
  function persistSms(next: any) { setSmsCfg(next); store.setSettings({ sms: next }); }   // keep a local copy too
  async function releaseNumber() {
    if (!(await confirmDialog({ title: "Release your texting number?", body: "Customers can no longer reach you on it, and the number can't be recovered.", confirmLabel: "Release number", danger: true }))) return;
    setErr(""); setSmsBusy(true);
    try {
      if (sms) await DB.sms.release();
      persistSms({ number: null, forwardTo: smsCfg.forwardTo, enabled: false });
      store.addActivity("Texting number released");
    } catch (e: any) { setErr(String((e && e.message) || e)); } finally { setSmsBusy(false); }
  }
  async function saveForwarding(patch: any) {
    const next = { ...smsCfg, forwardTo: fwdInput.trim(), ...patch };
    setErr("");
    try { if (sms) await DB.sms.setForwarding({ forwardTo: next.forwardTo, enabled: next.enabled }); }
    catch (e: any) { setErr(String((e && e.message) || e)); return; }   // never record a forwarding the server didn't accept
    persistSms(next);
  }
  const CREDITS: any = (window as any).VFP_CREDITS;   // optional, separate PR — don't hard-depend
  const credits = CREDITS && typeof CREDITS.get === "function" ? CREDITS.get(store) : null;

  return (
    <div className="page fade-up" style={{ maxWidth: 1100 }}>
      <h1 className="display" style={{ fontSize: 28, margin: "0 0 4px" }}>Messages</h1>
      <p className="muted" style={{ margin: "0 0 18px" }}>Customer conversations from their Hub{cloud ? " — replies show up on their portal" : ""}, two-way texting with customers, and direct support with the Fence Works team.</p>

      <div className="cat-tabs" style={{ marginBottom: 16 }}>
        <button className={"filter-chip" + (tab === "customers" ? " on" : "")} onClick={() => setTab("customers")}>Customers</button>
        <button className={"filter-chip" + (tab === "texts" ? " on" : "")} onClick={() => setTab("texts")}>Texts</button>
        <button className={"filter-chip" + (tab === "team" ? " on" : "")} onClick={() => setTab("team")}>Fence Works Team</button>
      </div>

      {tab !== "texts" && (
      <div className={"msg-layout" + (tab === "team" ? " solo" : "") + (narrow && tab === "customers" ? (pane === "detail" ? " pane-detail" : " pane-list") : "")}>
        {tab === "customers" && (
          <aside className="msg-list card" style={{ padding: 0 }}>
            {listErr && !loading && (
              <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--line)" }}>
                <p style={{ color: "var(--red)", fontSize: 12.5, margin: "0 0 6px" }}>Couldn't load messages — {listErr}</p>
                <button className="btn btn-outline btn-sm" onClick={() => { setLoading(true); refreshList(); }}>Retry</button>
              </div>
            )}
            {loading
              ? <p className="muted" style={{ fontSize: 13, textAlign: "center", padding: 24 }}>Loading conversations…</p>
              : threads.length === 0
                ? <EmptyState icon="user" title="No customers yet" sub="Customer conversations show up here once you add a customer or one messages you from their Hub." action={{ label: "Go to Customers", icon: "user", onClick: () => go("customers") }} />
                : threads.map((t: any) => (
                  <button key={t.id} className={"msg-thread" + (active && t.id === active.id ? " on" : "")} onClick={() => { setActiveId(t.id); setPane("detail"); }}>
                    <div className="cust-ava" style={{ width: 34, height: 34, fontSize: 14, position: "relative" }}>
                      {(t.name || "?").charAt(0).toUpperCase()}
                      {t.unread && <span style={{ position: "absolute", top: -2, right: -2, width: 10, height: 10, borderRadius: 99, background: "var(--red)", border: "2px solid var(--surface)" }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.name}</div>
                      <div className="muted" style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {t.kind === "new" ? "Start a conversation" : (t.lastAt ? new Date(t.lastAt).toLocaleString() : t.sub)}
                      </div>
                    </div>
                  </button>
                ))}
          </aside>
        )}

        <section className="msg-panel card" style={{ padding: 0 }}>
          {active ? (
            <>
              <div className="msg-head">
                <div className="row gap-10" style={{ alignItems: "center", minWidth: 0 }}>
                  {narrow && tab === "customers" && <button className="btn btn-ghost btn-sm pane-back" aria-label="Back to conversations" onClick={() => setPane("list")}><Icon name="chevright" size={14} style={{ transform: "rotate(180deg)" }} /></button>}
                  {tab === "team" ? <LogoMark size={26} /> : <div className="cust-ava" style={{ width: 32, height: 32, fontSize: 13 }}>{(active.name || "?").charAt(0).toUpperCase()}</div>}
                  <div style={{ minWidth: 0 }}><div style={{ fontWeight: 700, fontSize: 14.5 }}>{active.name}</div><div className="muted" style={{ fontSize: 12 }}>{active.sub}</div></div>
                </div>
                {/* the customer named a day → one tap moves their scheduled job there */}
                {tab === "customers" && active.customerId && (() => {
                  const last = activeMsgs.filter((m: any) => !m.mine).slice(-1)[0];
                  const when = last ? parseWhen(last.body) : null;
                  const wo = when ? (store.workOrders || []).find((w: any) => w.customerId === active.customerId && (w.status === "scheduled" || w.status === "unscheduled")) : null;
                  if (!when || !wo) return null;
                  const same = String(wo.scheduledStart || "").slice(0, 10) === when.ymd;
                  return same ? null : (
                    <button className="btn btn-outline btn-sm" title={"They mentioned " + when.label + " — move " + (wo.customer || "the job") + " there"}
                      onClick={() => { moveJob(store, wo, when.ymd, wo.crewId); notify("Moved to " + when.label + " — tell them it's booked."); }}>
                      <Icon name="calendar" size={13} />Move job to {when.label}
                    </button>
                  );
                })()}
                {tab === "customers" && active.customerId && <button className="btn btn-ghost btn-sm" onClick={() => go("customers", { focus: active.customerId })}><Icon name="user" size={13} />Journey</button>}
              </div>
              <div className="msg-body" ref={bodyRef}>
                {msgsErr && tab === "customers" && (
                  <div style={{ padding: "10px 0" }}>
                    <p style={{ color: "var(--red)", fontSize: 12.5, margin: "0 0 6px" }}>Couldn't load messages — {msgsErr}</p>
                    <button className="btn btn-outline btn-sm" onClick={() => { setMsgsErr(""); refreshMsgs(); }}>Retry</button>
                  </div>
                )}
                {activeMsgs.length === 0
                  ? (msgsErr && tab === "customers" ? null : <div className="msg-empty"><EmptyState icon="mail" title="No messages yet" sub="Say hello — it reaches their Customer Hub." /></div>)
                  : activeMsgs.map((m: any, i: number) => (
                    <div key={i} style={{ alignSelf: m.mine ? "flex-end" : "flex-start", maxWidth: "78%" }}>
                      <div className={"hub-msg " + (m.mine ? "me" : "company")} style={{ alignSelf: m.mine ? "flex-end" : "flex-start" }}>
                        {m.image && <a href={m.image} target="_blank" rel="noopener"><img src={m.image} alt="" style={{ maxWidth: 220, borderRadius: 8, display: "block", marginBottom: m.body ? 6 : 0 }} /></a>}
                        {m.body}
                      </div>
                      {m.at && <div className="faint" style={{ fontSize: 10.5, margin: "2px 4px 0", textAlign: m.mine ? "right" : "left" }}>{fmtMsgTime(m.at)}</div>}
                    </div>
                  ))}
              </div>
              {err && <p style={{ color: "var(--red)", fontSize: 12, padding: "0 14px" }}>{err}</p>}
              <div className="msg-compose">
                {tab !== "team" && <>
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files && e.target.files[0]; if (f) attachPhoto(f); e.currentTarget.value = ""; }} />
                  <button className="btn btn-ghost" title="Attach a photo" onClick={() => fileRef.current && fileRef.current.click()}><Icon name="image" size={16} /></button>
                </>}
                <input className="input" placeholder={tab === "team" ? "Message the Fence Works team…" : "Reply to " + active.name + "…"} value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === "Enter") send(); }} />
                <button className="btn btn-primary" onClick={() => send()}><Icon name="send" size={15} /></button>
              </div>
            </>
          ) : <div className="msg-empty" style={{ padding: 60 }}><EmptyState icon="mail" title="Select a conversation" sub="Pick a customer on the left." /></div>}
        </section>
      </div>
      )}

      {tab === "texts" && (
        !paid
          ? (
            <div className="card pad" style={{ textAlign: "center" }}>
              <EmptyState icon="lock" title="Texting is a paid feature" sub="Get a business texting number and message customers two-ways, right from here. Available on Starter and up." />
              <button className="btn btn-primary" onClick={() => go("account", { tab: "plan" })}><Icon name="bolt" size={15} />See plans &amp; upgrade</button>
            </div>
          )
          : !a2pActive
            ? (
              /* ---- A2P 10DLC registration: short business profile + live
                 status badge. Until the registration is APPROVED, outbound
                 texting is blocked by carriers — so this is the gate before the
                 thread UI. (Replaces the old direct "issue a number" flow, which
                 carriers would block pre-registration anyway.) ---- */
              <SmsRegistration store={store} DB={DB} live={sms} />
            )
            : (
              <>
                {/* active number + forwarding controls */}
                <div className="card pad" style={{ marginBottom: 16 }}>
                  <div className="row between" style={{ flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                    <span className="row gap-8 kicker kicker-teal"><Icon name="phone" size={14} />Your texting number</span>
                    <span className="mono" style={{ fontSize: 14, fontWeight: 700 }}>{smsCfg.number}</span>
                  </div>
                  <label className="toggle-row" onClick={() => saveForwarding({ enabled: !smsCfg.enabled })} style={{ marginBottom: fwdInput || smsCfg.enabled ? 10 : 0 }}>
                    <span className={"sw" + (smsCfg.enabled ? " on" : "")}><span className="knob" /></span>
                    <span style={{ fontSize: 13.5, fontWeight: 600 }}>Forward incoming texts to my cell</span>
                  </label>
                  {(smsCfg.enabled || fwdInput) && (
                    <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
                      <input className="input" type="tel" placeholder="(555) 555-5555" value={fwdInput} onChange={e => setFwdInput(e.target.value)} style={{ maxWidth: 220 }} />
                      <button className="btn btn-outline btn-sm" onClick={() => saveForwarding({})}>Save</button>
                      <span className="faint" style={{ fontSize: 11.5 }}>Reply from your cell and we relay it out through your business number.</span>
                    </div>
                  )}
                  {err && <p style={{ color: "var(--red)", fontSize: 12, marginTop: 10 }}>{err}</p>}
                  <p className="faint" style={{ fontSize: 11, margin: "12px 0 0" }}>
                    Each text uses one message credit. Replies, STOP/HELP opt-outs and carrier compliance are handled automatically.
                    {credits != null && <> {credits} credits left.</>}
                    {" · "}<a href="#" onClick={(e) => { e.preventDefault(); releaseNumber(); }}>Release number</a>
                  </p>
                </div>

                {/* thread list + conversation, mirroring the Customers tab */}
                <div className="msg-layout">
                  <aside className="msg-list card" style={{ padding: 0 }}>
                    {!sms
                      ? <p className="muted" style={{ fontSize: 13, textAlign: "center", padding: 24 }}>Texting needs the live service.</p>
                      : smsRows.length === 0
                        ? <EmptyState icon="chat" title="No texts yet" sub="When a customer texts your number it shows up here, threaded by person." />
                        : smsRows.map((t: any) => (
                          <button key={t.id} className={"msg-thread" + (active && t.id === active.id ? " on" : "")} onClick={() => setSmsActiveId(t.id)}>
                            <div className="cust-ava" style={{ width: 34, height: 34, fontSize: 14, position: "relative" }}>
                              {(t.name || "?").charAt(0).toUpperCase()}
                              {t.unread && <span style={{ position: "absolute", top: -2, right: -2, width: 10, height: 10, borderRadius: 99, background: "var(--red)", border: "2px solid var(--surface)" }} />}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.name}</div>
                              <div className="muted" style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {String(t.id).startsWith("new-") ? "Start a conversation" : (t.lastAt ? new Date(t.lastAt).toLocaleString() : t.sub)}
                              </div>
                            </div>
                          </button>
                        ))}
                  </aside>

                  <section className="msg-panel card" style={{ padding: 0 }}>
                    {active ? (
                      <>
                        <div className="msg-head">
                          <div className="row gap-10" style={{ alignItems: "center", minWidth: 0 }}>
                            <div className="cust-ava" style={{ width: 32, height: 32, fontSize: 13 }}>{(active.name || "?").charAt(0).toUpperCase()}</div>
                            <div style={{ minWidth: 0 }}><div style={{ fontWeight: 700, fontSize: 14.5 }}>{active.name}</div><div className="muted" style={{ fontSize: 12 }}>{active.sub || active.phone}</div></div>
                          </div>
                          {active.customerId && <button className="btn btn-ghost btn-sm" onClick={() => go("customers", { focus: active.customerId })}><Icon name="user" size={13} />Journey</button>}
                        </div>
                        <div className="msg-body" ref={bodyRef}>
                          {activeMsgs.length === 0
                            ? <div className="msg-empty"><EmptyState icon="chat" title="No texts yet" sub="Send a text — it goes out from your business number." /></div>
                            : activeMsgs.map((m: any, i: number) => (
                              <div key={i} style={{ alignSelf: m.mine ? "flex-end" : "flex-start", maxWidth: "78%" }}>
                                <div className={"hub-msg " + (m.mine ? "me" : "company")} style={{ alignSelf: m.mine ? "flex-end" : "flex-start" }}>{m.body}</div>
                                {m.at && <div className="faint" style={{ fontSize: 10.5, margin: "2px 4px 0", textAlign: m.mine ? "right" : "left" }}>{fmtMsgTime(m.at)}</div>}
                              </div>
                            ))}
                        </div>
                        {err && <p style={{ color: "var(--red)", fontSize: 12, padding: "0 14px" }}>{err}</p>}
                        <div className="msg-compose">
                          <input className="input" placeholder={"Text " + active.name + "…"} value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === "Enter") send(); }} />
                          <button className="btn btn-primary" onClick={() => send()}><Icon name="send" size={15} /></button>
                        </div>
                      </>
                    ) : <div className="msg-empty" style={{ padding: 60 }}><EmptyState icon="chat" title="Select a conversation" sub="Pick a customer on the left, or text a customer from their detail page." /></div>}
                  </section>
                </div>
              </>
            )
      )}
    </div>
  );
}
function lastAt(msgs) { return msgs.length ? (msgs[msgs.length - 1].at || 0) : 0; }
function fmtMsgTime(at: any) {
  const d = new Date(at); if (isNaN(d.getTime())) return "";
  const now = new Date(); const sameDay = d.toDateString() === now.toDateString();
  const t = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return sameDay ? t : d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " · " + t;
}

Object.assign(window, { MessagesPage });

export { MessagesPage, lastAt };
